import { faker } from "@faker-js/faker";
import { format } from "date-fns";

import {
  fakeAttendeeTypes,
  fakeBaseUserAvatarProps,
  fakeBaseUserType,
  fakeEventRecurrence,
  fakeIllustrationName,
  fakePronouns,
  fakeProviderType,
} from "@/__mocks__/mockBaseTypes";
import { merge } from "@/__mocks__/mockHelpers";

const fakeAttendee = (overrides?: DeepPartial<Attendee>): Attendee => {
  const attendee: Attendee = {
    ...fakeBaseUserAvatarProps(),
    pronouns: fakePronouns({}),
    subtitle: faker.person.jobType(),
  };
  return merge(attendee, overrides);
};

const fakeGroupClassSeriesHost = (
  overrides?: DeepPartial<GroupClassSeriesHost>,
): GroupClassSeriesHost => {
  const host: GroupClassSeriesHost = {
    email: faker.internet.email(),
    externalId: faker.string.uuid(),
    firstName: faker.person.firstName(),
    lastName: faker.person.lastName(),
    pronouns: fakePronouns({}),
    providerType: fakeProviderType(),
    type: fakeBaseUserType(),
  };
  return merge(host, overrides);
};

const fakeRecurrenceDisplayString = () => {
  const prefix = faker.helpers.arrayElement([
    "Every other",
    "",
    "Every 4th",
    "Every",
  ]);
  const weekday = faker.date.weekday();
  const weekPortion = [prefix, weekday].join(" ").trim();

  const timePortion = [
    format(faker.date.anytime(), "h:mm a").toLowerCase(),
    format(faker.date.anytime(), "h:mm a").toLowerCase(),
  ]
    .join(" - ")
    .trim();
  return [weekPortion, timePortion].join(", ");
};

const fakeGroupClass = (overrides?: DeepPartial<GroupClass>): GroupClass => {
  // eslint-disable-next-line no-restricted-syntax -- group classes as defined by the backend API have >15 properties
  const groupClass: GroupClass = {
    attendeeTypes: fakeAttendeeTypes(),
    description: faker.word.words({ count: { max: 100, min: 3 } }),
    duration: faker.number.int({ max: 1000, min: 15 }),
    frequency: fakeEventRecurrence(),
    groupClassExternalId: faker.string.uuid(),
    hosts: faker.helpers.multiple(() => fakeGroupClassSeriesHost(), {
      count: faker.number.int({ max: 10, min: 1 }),
    }),
    iconName: fakeIllustrationName(),
    isPromoted: faker.datatype.boolean(),
    isSensitive: faker.datatype.boolean(),
    isUserRegistered: faker.datatype.boolean(),
    maximumAttendees: faker.number.int(),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    mustAttendAll: faker.datatype.boolean(),
    nextStartDate: faker.date.anytime().toISOString(),
    registeredUsersCount: faker.number.int(),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    showMaximumAttendees: faker.datatype.boolean(),
    startDate: faker.date.anytime().toISOString(),
    title: faker.word.words({ count: { max: 5, min: 2 } }),
  };
  return merge(groupClass, overrides);
};

const fakeGroupClassSeries = (
  overrides?: DeepPartial<GroupClassSeries>,
): GroupClassSeries => {
  const series: GroupClassSeries = {
    ...fakeGroupClass(),
    recurrenceDisplayString: fakeRecurrenceDisplayString(),
  };
  return merge(series, overrides);
};

const fakeGroupClassSeriesList = (
  overrides?: DeepPartial<GroupClassSeries>,
  count?: { count: { max: number; min: number } },
  // eslint-disable-next-line max-params
): GroupClassSeries[] => {
  const defaultCount = {
    count: { max: 500, min: 100 },
  };
  return faker.helpers.multiple(
    () => fakeGroupClassSeries(overrides),
    count ?? defaultCount,
  );
};

export {
  fakeAttendee,
  fakeGroupClass,
  fakeGroupClassSeries,
  fakeGroupClassSeriesList,
};
